import React from 'react';
import { MapPinIcon, TruckIcon, ClockIcon, NavigationIcon, MessageSquareIcon, CheckCircleIcon, FileTextIcon, InfoIcon, CalendarIcon, UserIcon, BuildingIcon } from 'lucide-react';
import { useMessages } from '../../messaging/MessageContext';
import { JobMessageButton } from '../../messaging/JobMessageButton';
interface Job {
  id: string;
  date: string;
  customer: string;
  jobName: string;
  jobNumber: string;
  startTime: string;
  travelTime: string;
  loadLocation: {
    name: string;
    address: string;
    coordinates: {
      lat: number;
      lng: number;
    };
  };
  dumpLocation: {
    name: string;
    address: string;
    coordinates: {
      lat: number;
      lng: number;
    };
  };
  material: string;
  truckType: string;
  goals: {
    loads: number;
    tonnage?: number;
  };
  foreman: {
    name: string;
    phone: string;
  };
  notes: {
    contractor?: string;
    dispatch?: string;
  };
  status: 'pending' | 'in-progress' | 'completed';
  ticketNumber?: string;
  geoPunchRequired?: boolean;
  geoPunchDistance?: number;
}
interface JobCardProps {
  job: Job;
  onStartJob: (jobId: string) => void;
  onCompleteJob: (jobId: string) => void;
  onViewMap: (location: {
    lat: number;
    lng: number;
  }) => void;
  onCallForeman: (phone: string) => void;
  isActive: boolean;
  isUpcoming?: boolean;
  isCompleted?: boolean;
}
export const JobCard = ({
  job,
  onStartJob,
  onCompleteJob,
  onViewMap,
  onCallForeman,
  isActive,
  isUpcoming = false,
  isCompleted = false
}: JobCardProps) => {
  const {
    getJobContacts
  } = useMessages();
  const formatDate = (dateString: string) => {
    const options: Intl.DateTimeFormatOptions = {
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    };
    return new Date(dateString).toLocaleDateString('en-US', options);
  };
  const handleMessageForeman = () => {
    onCallForeman(job.id);
  };
  return <div className={`bg-white rounded-lg shadow overflow-hidden ${isActive ? 'border-2 border-blue-500' : ''}`} data-id="element-2336">
      {/* Job Header */}
      <div className="p-4 bg-gray-50 border-b" data-id="element-2337">
        <div data-id="element-2338">
          <h3 className="font-semibold text-lg" data-id="element-2339">{job.jobName}</h3>
          <div className="flex flex-wrap items-center gap-2 text-sm text-gray-600" data-id="element-2340">
            <span data-id="element-2341">Job #{job.jobNumber}</span>
            {job.ticketNumber && <>
                <span data-id="element-2342">•</span>
                <span data-id="element-2343">Ticket #{job.ticketNumber}</span>
              </>}
          </div>
        </div>
        <div className="flex flex-wrap gap-2 mt-2" data-id="element-2344">
          <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-0.5 rounded-full flex items-center gap-1" data-id="element-2345">
            <CalendarIcon size={12} data-id="element-2346" />
            {formatDate(job.date)}
          </span>
          <span className="bg-purple-100 text-purple-800 text-xs font-medium px-2.5 py-0.5 rounded-full flex items-center gap-1" data-id="element-2347">
            <ClockIcon size={12} data-id="element-2348" />
            {job.startTime}
          </span>
          <span className="bg-green-100 text-green-800 text-xs font-medium px-2.5 py-0.5 rounded-full flex items-center gap-1" data-id="element-2349">
            <TruckIcon size={12} data-id="element-2350" />
            {job.truckType}
          </span>
          <span className="bg-yellow-100 text-yellow-800 text-xs font-medium px-2.5 py-0.5 rounded-full" data-id="element-2351">
            {job.material}
          </span>
        </div>
      </div>

      {/* Customer Info */}
      <div className="p-4 border-b" data-id="element-2352">
        <div className="flex items-start gap-2" data-id="element-2353">
          <BuildingIcon size={18} className="text-gray-400 mt-0.5" data-id="element-2354" />
          <div data-id="element-2355">
            <p className="text-sm text-gray-500" data-id="element-2356">Customer</p>
            <p className="font-medium" data-id="element-2357">{job.customer}</p>
          </div>
        </div>
      </div>

      {/* Job Details - Always Visible */}
      <div className="p-4 border-b" data-id="element-2358">
        {/* Locations */}
        <div className="space-y-4 mb-4" data-id="element-2359">
          <div className="flex items-start gap-3" data-id="element-2360">
            <div className="bg-blue-100 p-2 rounded-full" data-id="element-2361">
              <MapPinIcon size={18} className="text-blue-600" data-id="element-2362" />
            </div>
            <div className="flex-1" data-id="element-2363">
              <p className="font-medium text-sm" data-id="element-2364">Load Location</p>
              <p className="text-gray-700" data-id="element-2365">{job.loadLocation.name}</p>
              <p className="text-sm text-gray-500" data-id="element-2366">
                {job.loadLocation.address}
              </p>
              <button onClick={() => onViewMap(job.loadLocation.coordinates)} className="mt-1 text-blue-600 text-sm flex items-center gap-1" data-id="element-2367">
                <NavigationIcon size={14} data-id="element-2368" />
                Navigate
              </button>
            </div>
          </div>
          <div className="flex items-start gap-3" data-id="element-2369">
            <div className="bg-green-100 p-2 rounded-full" data-id="element-2370">
              <MapPinIcon size={18} className="text-green-600" data-id="element-2371" />
            </div>
            <div className="flex-1" data-id="element-2372">
              <p className="font-medium text-sm" data-id="element-2373">Dump Location</p>
              <p className="text-gray-700" data-id="element-2374">{job.dumpLocation.name}</p>
              <p className="text-sm text-gray-500" data-id="element-2375">
                {job.dumpLocation.address}
              </p>
              <button onClick={() => onViewMap(job.dumpLocation.coordinates)} className="mt-1 text-blue-600 text-sm flex items-center gap-1" data-id="element-2376">
                <NavigationIcon size={14} data-id="element-2377" />
                Navigate
              </button>
            </div>
          </div>
        </div>

        {/* Travel Time from Dispatch */}
        <div className="mb-4 p-3 bg-blue-50 rounded-lg" data-id="element-2378">
          <div className="flex items-center gap-2" data-id="element-2379">
            <ClockIcon size={18} className="text-blue-600" data-id="element-2380" />
            <div data-id="element-2381">
              <p className="font-medium" data-id="element-2382">Travel Time (From Dispatch)</p>
              <p className="text-blue-700" data-id="element-2383">{job.travelTime} minutes</p>
              <p className="text-xs text-blue-600 mt-1" data-id="element-2384">
                This time will be automatically added to your timecard when job
                is completed
              </p>
            </div>
          </div>
        </div>

        {/* Job Goals */}
        <div className="mb-4 p-3 bg-gray-50 rounded-lg" data-id="element-2385">
          <h4 className="font-medium mb-2" data-id="element-2386">Job Goals</h4>
          <div className="grid grid-cols-2 gap-4" data-id="element-2387">
            <div data-id="element-2388">
              <p className="text-sm text-gray-500" data-id="element-2389">Target Loads</p>
              <p className="font-medium" data-id="element-2390">{job.goals.loads} loads</p>
            </div>
            {job.goals.tonnage && <div data-id="element-2391">
                <p className="text-sm text-gray-500" data-id="element-2392">Target Tonnage</p>
                <p className="font-medium" data-id="element-2393">{job.goals.tonnage} tons</p>
              </div>}
          </div>
        </div>

        {/* Foreman Information */}
        

        {/* Notes */}
        {(job.notes.contractor || job.notes.dispatch) && <div className="space-y-3 mb-4" data-id="element-2394">
            <h4 className="font-medium flex items-center gap-2" data-id="element-2395">
              <FileTextIcon size={16} data-id="element-2396" />
              Job Notes
            </h4>
            {job.notes.contractor && <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-md" data-id="element-2397">
                <p className="text-xs font-medium text-yellow-800 mb-1 flex items-center gap-1" data-id="element-2398">
                  <BuildingIcon size={12} data-id="element-2399" />
                  FROM CONTRACTOR
                </p>
                <p className="text-sm text-yellow-900" data-id="element-2400">
                  {job.notes.contractor}
                </p>
              </div>}
            {job.notes.dispatch && <div className="p-3 bg-blue-50 border border-blue-200 rounded-md" data-id="element-2401">
                <p className="text-xs font-medium text-blue-800 mb-1 flex items-center gap-1" data-id="element-2402">
                  <TruckIcon size={12} data-id="element-2403" />
                  FROM DISPATCH
                </p>
                <p className="text-sm text-blue-900" data-id="element-2404">{job.notes.dispatch}</p>
              </div>}
          </div>}

        {/* Geo Punch Requirement */}
        {job.geoPunchRequired && <div className="mb-4 p-3 bg-blue-50 rounded-lg" data-id="element-2405">
            <div className="flex items-center gap-2" data-id="element-2406">
              <MapPinIcon size={18} className="text-blue-600" data-id="element-2407" />
              <div data-id="element-2408">
                <p className="font-medium" data-id="element-2409">Geo Punch Required</p>
                <p className="text-blue-700" data-id="element-2410">
                  You must be within {job.geoPunchDistance || 150} feet of the
                  job location to clock in
                </p>
              </div>
            </div>
          </div>}
      </div>

      {/* Action Footer */}
      <div className="p-3 bg-gray-50 border-t" data-id="element-2411">
        {isCompleted ? <div className="flex items-center justify-center text-green-600 gap-2 py-1" data-id="element-2412">
            <CheckCircleIcon size={18} data-id="element-2413" />
            <span className="font-medium" data-id="element-2414">Completed</span>
          </div> : isUpcoming ? <div className="flex items-center justify-center text-blue-600 gap-2 py-1" data-id="element-2415">
            <CalendarIcon size={18} data-id="element-2416" />
            <span className="font-medium" data-id="element-2417">
              Scheduled for {formatDate(job.date)}
            </span>
          </div> : isActive ? <button onClick={() => onCompleteJob(job.id)} className="w-full py-2 bg-green-600 hover:bg-green-700 text-white rounded-md font-medium flex items-center justify-center gap-2" data-id="element-2418">
            <CheckCircleIcon size={18} data-id="element-2419" />
            Complete Job
          </button> : <button onClick={() => onStartJob(job.id)} className="w-full py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md font-medium flex items-center justify-center gap-2" data-id="element-2420">
            <ClockIcon size={18} data-id="element-2421" />
            View job
          </button>}
      </div>
    </div>;
};